import { ArrowLeft } from "lucide-react";
import { Link, Navigate, Outlet, useParams } from "react-router-dom";

import { Button } from "@/components/_atoms/button";
import { Loading } from "@/components/_molecules/loading-view";
import { DetailPageHeaderNav } from "@/components/_organisms/detail-view/detail-page-header-nav";
import { FeatureFlagToggle } from "@/domain/_shared/feature-flag/components/feature-flag-toggle";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { ModuleActionGuard } from "@/domain/_shared/module-access/components/module-action-guard";
import { useEntityPermissions } from "@/domain/_shared/permission/hooks/use-entity-permissions";
import { MunicipalPropertyYearAddDialog } from "@/domain/municipal-property/components/municipal-property-year-add-dialog";
import { MunicipalPropertyYearNavigation } from "@/domain/municipal-property/components/municipal-property-year-navigation";
import { useMunicipalPropertyRegister } from "@/domain/municipal-property/hooks/api/use-municipal-property-register";
import { useMunicipalPropertyRegisterContent } from "@/domain/municipal-property/hooks/api/use-municipal-property-register-content";
import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";

export const MunicipalPropertyRegisterDetailPage = () => {
  const { registerId = "", propertyYearId }: { registerId?: string; propertyYearId?: string } = useParams<{ registerId: string; propertyYearId: string }>();
  const { currentWorkspace }: ReturnType<typeof useCurrentWorkspaceContext> = useCurrentWorkspaceContext();
  const { t } = useDomainTranslation("municipal-property");
  const { data: register, error, isLoading } = useMunicipalPropertyRegister(registerId);
  const {
    data: content,
    error: contentError,
    isLoading: isContentLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  }: ReturnType<typeof useMunicipalPropertyRegisterContent> = useMunicipalPropertyRegisterContent({ registerId });
  const { canWrite }: ReturnType<typeof useEntityPermissions> = useEntityPermissions({
    entityId: registerId,
    resource: "municipalPropertyRegister",
  });

  if (isLoading || isContentLoading) {
    return <Loading />;
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {error || (!content && contentError) || !register || !content ? (
        <div className="flex flex-col gap-4 p-6">
          <Link to="../registers" className="flex items-center gap-1 text-sm text-muted-foreground hover:underline">
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
            {t("register.detail.backToList")}
          </Link>
          <p className="text-sm text-muted-foreground">{t("register.detail.notFound")}</p>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-6">
          <DetailPageHeaderNav
            titleForm={
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-1">
                  <h1 className="text-lg font-semibold">{t("register.detail.heading", { year: register.year })}</h1>
                  <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                    <dt className="text-muted-foreground">{t("register.detail.year")}</dt>
                    <dd className="tabular-nums">{register.year}</dd>
                  </dl>
                </div>
              </div>
            }
            tabs={
              <Link to="../registers" className="flex items-center gap-1 text-sm text-muted-foreground hover:underline">
                <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
                {t("register.detail.backToList")}
              </Link>
            }
            tabActions={
              <>
                {canWrite ? (
                  <ModuleActionGuard module="MUNICIPAL_PROPERTY" action="create">
                    <MunicipalPropertyYearAddDialog registerId={register.id} workspaceId={currentWorkspace.id} />
                  </ModuleActionGuard>
                ) : null}
              </>
            }
          />
          <div className="flex min-h-0 flex-1 flex-col gap-4 px-6 pb-6 md:flex-row">
            <aside className="flex min-h-0 flex-col justify-between gap-3 md:w-60 md:shrink-0 md:border-r md:pr-4">
              <div className="flex min-h-0 flex-1 flex-col gap-3">
                <MunicipalPropertyYearNavigation yearList={content.municipalPropertyYearList} hasNextPage={hasNextPage} />
                {hasNextPage ? (
                  <div className="flex flex-col items-center gap-2">
                    {isFetchNextPageError ? <p role="alert">{t("register.property.loadMoreError")}</p> : null}
                    <Button
                      variant="outline"
                      size="xs"
                      disabled={isFetchingNextPage}
                      onClick={(): void => void fetchNextPage()}
                    >
                      {t(isFetchNextPageError ? "register.property.retry" : "catalog.loadMore")}
                    </Button>
                  </div>
                ) : null}
              </div>
              <FeatureFlagToggle flag="enable-trash">
                <Link
                  className="shrink-0 border-t pt-3 text-sm text-muted-foreground hover:underline"
                  to="../registers/years/trash"
                >
                  {t("register.property.trash")}
                </Link>
              </FeatureFlagToggle>
            </aside>
            <div className="min-h-0 min-w-0 flex-1">
              {propertyYearId ? (
                <Outlet key={propertyYearId} />
              ) : content.municipalPropertyYearList.length ? (
                <Navigate to={`${content.municipalPropertyYearList[0].id}/data`} replace />
              ) : (
                <div className="flex flex-1 flex-col gap-4">
                  <p className="text-sm text-muted-foreground">{t("register.property.empty")}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
